import { useAuthStore } from '../../../stores/authStore';
import { getUser } from "./oidcConfig";
import { testMgr } from '../utils/oidcConfig';
import { isOidcSpecialRoute, shouldSkipSSO } from './oidcRouteHelper';

/**
 * 初始化身份验证状态
 * 检查现有token，决定是否需要重定向到SSO登录
 *
 * 关键改进：
 * 1. 识别 OIDC 特殊路由（/callback 等），直接返回避免循环
 * 2. 优先检查 Zustand 状态，避免重复初始化
 * 3. 依赖 UserManager 作为单一真源
 */
export const initAuth = async (): Promise<void> => {
  // ✅ 关键修复 1：检测 OIDC 特殊路由，立即返回
  if (isOidcSpecialRoute()) {
    return;
  }

  const authStore = useAuthStore.getState();

  try {
    // 开发环境免登录模式
    if (shouldSkipSSO()) {
      // 检查 Zustand persist 或乐观恢复是否已设置认证状态
      if (authStore.isAuthenticated && authStore.user) {
        return;
      }

      // 未认证，直接返回，让 RouteGuard 处理重定向到 /login
      // 用户将在登录页面选择登录方式
      return;
    }

    // ✅ 关键修复 2：支持乐观恢复 (Optimistic Restoration)
    // 如果已经由 main.tsx 中的 performOptimisticRestore 完成了基本信息填充
    // 我们不再阻塞首屏渲染，但在后台静默执行严谨校验
    if (authStore.isAuthenticated && authStore.user) {
      // 检查是否需要“积极续期” (Proactive Silent Renew)
      // 如果 Token 即将过期（例如 10 分钟内），在后台触发刷新
      const user = await getUser();
      if (user && !user.expired) {
        const expiresIn = user.expires_at! - Math.floor(Date.now() / 1000);
        if (expiresIn < 600) { // < 10 分钟
          console.log('[authInit] 🔄 Token 即将过期，触发积极续期...');
          testMgr.signinSilent().catch(err => console.warn('[authInit] 积极续期失败:', err));
        }
        return;
      }
    }

    // ✅ 关键修复 3：常规校验流程
    const user = await getUser();

    if (user && !user.expired) {
      authStore.loginWithOidc(user);
      return;
    }

    // 无有效会话且非乐观恢复成功，则重定向
    await testMgr.signinRedirect({ state: window.location.href });

  } catch (error) {
    if (authStore.isAuthenticated) {
      console.warn('[authInit] 校验失败，但由于已通过乐观认证，暂时允许停留界面:', error);
      return;
    }
    // 出错且无乐观认证时，重定向到登录
    await testMgr.signinRedirect({ state: window.location.href });
  }
};

/**
 * 清除身份验证状态
 * 用于处理token过期、无效token等情况
 */
export const clearAuth = (): void => {
  const authStore = useAuthStore.getState();
  authStore.logout();

  // 清除localStorage中的token
  localStorage.removeItem('access_token');
  localStorage.removeItem('id_token');
  localStorage.removeItem('tokenObj');
};

/**
 * 检查token是否过期
 * 在实现真正的JWT token时会很有用
 */
export const isTokenExpired = (tokenObj: any): boolean => {
  if (!tokenObj || !tokenObj.expires_at) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  return tokenObj.expires_at < now;
};
